import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBodyScrollLock } from "../lib/use-body-scroll-lock";
import { ONBOARDING_STEPS } from "../lib/onboarding-steps";

const BORDER_RADIUS = 10;
const CALLOUT_WIDTH = 224;
const CALLOUT_OFFSET = 16; // gap between spotlight and callout
const CALLOUT_ESTIMATED_HEIGHT = 155;

interface OnboardingTourProps {
  onComplete: () => void;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const [step, setStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const rafRef = useRef<number | null>(null);
  useBodyScrollLock();

  const current = ONBOARDING_STEPS[step];
  const isFirst = step === 0;
  const isLast = step === ONBOARDING_STEPS.length - 1;
  const hasTarget = current.highlightTarget !== null;

  useEffect(() => {
    if (!hasTarget) {
      setSpotlightRect(null);
      return;
    }

    if (current.route && location.pathname !== current.route) {
      navigate(current.route);
    }

    // rAF ensures DOM has painted after navigation
    rafRef.current = requestAnimationFrame(() => {
      const candidates = document.querySelectorAll<HTMLElement>(`[data-tour-target="${current.highlightTarget}"]`);
      const el = Array.from(candidates).find((e) => e.offsetParent !== null);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setSpotlightRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [step, hasTarget, current.highlightTarget, current.route, location.pathname, navigate]);

  const handleNext = () => {
    if (isLast) onComplete();
    else setStep((s) => s + 1);
  };

  const calloutShowBelow = spotlightRect ? spotlightRect.top < CALLOUT_ESTIMATED_HEIGHT + CALLOUT_OFFSET + 8 : false;
  const calloutLeft = spotlightRect
    ? Math.max(
        12,
        Math.min(
          spotlightRect.left + spotlightRect.width / 2 - CALLOUT_WIDTH / 2,
          window.innerWidth - CALLOUT_WIDTH - 12,
        ),
      )
    : 0;
  const calloutPosStyle = spotlightRect
    ? calloutShowBelow
      ? { top: spotlightRect.top + spotlightRect.height + CALLOUT_OFFSET }
      : { bottom: window.innerHeight - spotlightRect.top + CALLOUT_OFFSET + 9 }
    : {};

  return (
    <>
      {/* Dark overlay — visible as soon as a target step begins */}
      {hasTarget && (
        <div className="fixed inset-0 z-[60] pointer-events-none" style={{ background: "rgba(0,0,0,0.65)" }} />
      )}

      {/* Spotlight div — only once we know the rect */}
      {hasTarget && spotlightRect && (
        <div
          className="fixed z-[61] pointer-events-none"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            borderRadius: BORDER_RADIUS,
            boxShadow: `0 0 0 3px #3b82f6, 0 0 0 6px rgba(59,130,246,0.25)`,
          }}
        />
      )}

      {/* Callout card */}
      {hasTarget ? (
        spotlightRect ? (
          <div
            className="fixed z-[62] pointer-events-auto"
            style={{ width: CALLOUT_WIDTH, left: calloutLeft, ...calloutPosStyle }}
          >
            <CalloutCard
              step={step}
              total={ONBOARDING_STEPS.length}
              title={current.title}
              description={current.description}
              isFirst={isFirst}
              isLast={isLast}
              onNext={handleNext}
              onSkip={onComplete}
              showArrow={!calloutShowBelow}
            />
          </div>
        ) : null
      ) : (
        <div className="fixed z-[62] left-0 right-0 bottom-20 px-4 pointer-events-auto md:bottom-auto md:top-1/2 md:-translate-y-1/2">
          <div className="max-w-xs mx-auto">
            <CalloutCard
              step={step}
              total={ONBOARDING_STEPS.length}
              title={current.title}
              description={current.description}
              isFirst={isFirst}
              isLast={isLast}
              onNext={handleNext}
              onSkip={onComplete}
              showArrow={false}
            />
          </div>
        </div>
      )}
    </>
  );
}

interface CalloutCardProps {
  step: number;
  total: number;
  title: string;
  description: string;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
  showArrow: boolean;
}

function CalloutCard({
  step,
  total,
  title,
  description,
  isFirst,
  isLast,
  onNext,
  onSkip,
  showArrow,
}: CalloutCardProps) {
  return (
    <div>
      <div className="bg-white rounded-2xl p-4 shadow-2xl">
        {/* Step dots */}
        <div className="flex gap-1.5 justify-center mb-3">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-4 bg-blue-600" : "w-1.5 bg-gray-200"}`}
            />
          ))}
        </div>

        <h2 className="text-sm font-bold text-gray-900 text-center mb-1.5">{title}</h2>
        <p className="text-xs text-gray-500 text-center mb-3 leading-relaxed">{description}</p>

        <div className="flex items-center gap-2">
          {!isFirst && !isLast ? (
            <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
              Salta
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={onNext}
            className="flex-1 bg-blue-600 text-white text-xs font-semibold py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
          >
            {isFirst ? "Inizia →" : isLast ? "Inizia a usare WorkIn" : "Avanti →"}
          </button>
        </div>
      </div>

      {/* Downward arrow pointing to spotlight */}
      {showArrow && (
        <div
          className="mx-auto"
          style={{
            width: 0,
            height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "9px solid white",
            filter: "drop-shadow(0 3px 2px rgba(0,0,0,0.15))",
          }}
        />
      )}
    </div>
  );
}
